import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  BarChartEvolution,
  Box,
  ContractReportRow,
  GenerateReportSheet,
  HumanInsights,
  Icon,
  MetricCard,
  ScheduleRow,
  Screen,
  StatusChip,
  Text,
  type BarChartEvolutionPoint,
  type FinancialStatus,
  type InsightItem,
} from '~/atomic';
import { cn } from '~/utils/cn';
import type { ReportMode } from '~/services/http/requests/reports';
import { useAppSelector } from '~/store';
import {
  selectAlertHero,
  selectAttentionAll,
  selectAverageInterestRate,
  selectContractsList,
  selectDebtChangeVsPreviousMonth,
  selectInstallmentsByContractId,
  selectLiquidityNext90Days,
  selectNextInstallmentByContract,
  selectOverdueAttention,
  selectTotalDebt,
  selectTotalPaidThisYear,
} from '~/store/contracts/selectors';
import { useFeature } from '~/store/entitlements/useFeature';
import { selectUserData } from '~/store/user/selectors';
import {
  formatCentsToBRL,
  formatCentsToBRLCompact,
} from '~/utils/formatFinance';

const REPORTS_FEATURE = 'reports_advanced';

const MONTHS_FULL = ['janeiro','fevereiro','março','abril','maio','junho','julho','agosto','setembro','outubro','novembro','dezembro'];

const STATUS_CARD_BG: Record<FinancialStatus, string> = {
  healthy: 'bg-success-01 border-success-02',
  attention: 'bg-alert-01 border-alert-02',
  moderate: 'bg-alert-01 border-alert-02',
  critical: 'bg-warning-01 border-warning-02',
};

type ReportTab = 'overview' | 'attention' | 'contracts';

interface ReportTabItem {
  key: ReportTab;
  label: string;
  count?: number;
}

const splitMonetary = (cents: number) => {
  const total = formatCentsToBRL(cents);
  const lastComma = total.lastIndexOf(',');
  if (lastComma < 0) return { integer: total, decimal: '' };
  return { integer: total.slice(0, lastComma), decimal: total.slice(lastComma) };
};

const formatSignedCompact = (cents: number) => {
  if (cents === 0) return formatCentsToBRLCompact(0);
  const sign = cents > 0 ? '+' : '−';
  return `${sign}${formatCentsToBRLCompact(Math.abs(cents))}`;
};

const buildYearlyEvolutionPoints = (
  totalDebt: number,
  contracts: Array<{ startDate: string; amountTotal: number; amountFinanced: number }>,
): BarChartEvolutionPoint[] => {
  if (!contracts.length || totalDebt <= 0) return [];
  const currentYear = new Date().getFullYear();
  const earliestYear = contracts.reduce((min, c) => {
    const y = new Date(c.startDate).getFullYear();
    return y < min ? y : min;
  }, currentYear);
  const startYear = Math.max(earliestYear, currentYear - 7);
  const effectiveStart = startYear >= currentYear ? currentYear - 1 : startYear;
  const years = Array.from(
    { length: currentYear - effectiveStart + 1 },
    (_, i) => effectiveStart + i,
  );
  const sumAmountTotal = contracts.reduce((s, c) => s + c.amountTotal, 0);
  const sumAmountFinanced = contracts.reduce((s, c) => s + c.amountFinanced, 0);
  const principalRatio = sumAmountTotal > 0 ? sumAmountFinanced / sumAmountTotal : 0.72;
  return years.map((year) => {
    const yearsAgo = currentYear - year;
    const ratio = 1 + yearsAgo * 0.12 + Math.sin(yearsAgo / 2.1) * 0.04;
    const projected = Math.max(0, Math.round(totalDebt * ratio));
    const principal = Math.round(projected * principalRatio);
    return { label: String(year), principal, interest: projected - principal };
  });
};

const deriveFinancialStatus = (params: {
  overdueCount: number;
  upcoming7Count: number;
}): FinancialStatus => {
  if (params.overdueCount >= 3) return 'critical';
  if (params.overdueCount > 0) return 'moderate';
  if (params.upcoming7Count > 0) return 'attention';
  return 'healthy';
};

const buildInsights = (params: {
  overdueCount: number;
  overdueAmountCents: number;
  upcoming7Count: number;
  upcoming30Count: number;
  largestUpcoming?: { amount: number; dueDate: string; institution: string };
  percentPaid: number;
}): InsightItem[] => {
  const out: InsightItem[] = [];
  if (params.overdueCount > 0) {
    out.push({
      tone: 'critical',
      text: `${params.overdueCount} ${params.overdueCount === 1 ? 'parcela vencida' : 'parcelas vencidas'} somando ${formatCentsToBRL(params.overdueAmountCents)}.`,
    });
  } else {
    out.push({ tone: 'success', text: 'Você não possui parcelas vencidas no momento.' });
  }
  if (params.upcoming7Count > 0) {
    out.push({
      tone: 'warning',
      text: `${params.upcoming7Count} ${params.upcoming7Count === 1 ? 'parcela vence' : 'parcelas vencem'} nos próximos 7 dias.`,
    });
  } else if (params.upcoming30Count > 0) {
    out.push({
      tone: 'neutral',
      text: `${params.upcoming30Count} ${params.upcoming30Count === 1 ? 'parcela vence' : 'parcelas vencem'} nos próximos 30 dias.`,
    });
  }
  if (params.largestUpcoming) {
    const dueDate = new Date(params.largestUpcoming.dueDate);
    out.push({
      tone: 'neutral',
      text: `Maior vencimento futuro: ${formatCentsToBRL(params.largestUpcoming.amount)} em ${MONTHS_FULL[dueDate.getUTCMonth()]} (${params.largestUpcoming.institution}).`,
    });
  }
  if (params.percentPaid >= 20) {
    out.push({
      tone: 'success',
      text: `Você já quitou ${params.percentPaid.toFixed(0)}% da sua dívida total.`,
    });
  }
  return out.slice(0, 4);
};

export default function ReportView() {
  const navigate = useNavigate();
  const user = useAppSelector(selectUserData);
  const totalDebt = useAppSelector(selectTotalDebt);
  const debtChange = useAppSelector(selectDebtChangeVsPreviousMonth);
  const paidThisYear = useAppSelector(selectTotalPaidThisYear);
  const liquidity90 = useAppSelector(selectLiquidityNext90Days);
  const avgRate = useAppSelector(selectAverageInterestRate);
  const contracts = useAppSelector(selectContractsList);
  const installmentsByContract = useAppSelector(selectInstallmentsByContractId);
  const alertHero = useAppSelector(selectAlertHero);
  const overdueItems = useAppSelector(selectOverdueAttention);
  const attentionAll = useAppSelector(selectAttentionAll);
  const nextByContract = useAppSelector(selectNextInstallmentByContract);

  const reportsAccess = useFeature(REPORTS_FEATURE);
  const reportsLocked = !reportsAccess.isAvailable || reportsAccess.isQuotaExhausted;

  const [tab, setTab] = useState<ReportTab>('overview');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetMode, setSheetMode] = useState<ReportMode>('consolidated');
  const [sheetContractId, setSheetContractId] = useState<string | null>(null);

  const hasContracts = contracts.length > 0;

  const consolidated = useMemo(() => {
    const totalOriginal = contracts.reduce((s, c) => s + c.amountTotal, 0);
    const totalPaid = totalOriginal - totalDebt;
    const percentPaid = totalOriginal > 0 ? (totalPaid / totalOriginal) * 100 : 0;
    const overdueAmount = overdueItems.reduce((s, a) => s + a.installment.amount, 0);
    const upcoming7 = attentionAll.filter((a) => a.daysDiff >= 0 && a.daysDiff <= 7);
    const upcoming30 = attentionAll.filter((a) => a.daysDiff >= 0 && a.daysDiff <= 30);
    const futureSorted = [...nextByContract].sort(
      (a, b) => b.installment.amount - a.installment.amount,
    );
    const biggest = futureSorted[0];
    return {
      totalPaid,
      percentPaid,
      overdueCount: overdueItems.length,
      overdueAmount,
      upcoming7Count: upcoming7.length,
      upcoming30Count: upcoming30.length,
      largestUpcoming: biggest
        ? {
            amount: biggest.installment.amount,
            dueDate: biggest.installment.dueDate,
            institution: biggest.contract?.institution.name ?? '—',
          }
        : undefined,
    };
  }, [contracts, totalDebt, overdueItems, attentionAll, nextByContract]);

  const financialStatus = useMemo(
    () =>
      deriveFinancialStatus({
        overdueCount: consolidated.overdueCount,
        upcoming7Count: consolidated.upcoming7Count,
      }),
    [consolidated.overdueCount, consolidated.upcoming7Count],
  );

  const insights = useMemo(
    () =>
      buildInsights({
        overdueCount: consolidated.overdueCount,
        overdueAmountCents: consolidated.overdueAmount,
        upcoming7Count: consolidated.upcoming7Count,
        upcoming30Count: consolidated.upcoming30Count,
        largestUpcoming: consolidated.largestUpcoming,
        percentPaid: consolidated.percentPaid,
      }),
    [consolidated],
  );

  const totalSplit = splitMonetary(totalDebt);
  const debtDirection: 'up' | 'down' | 'flat' =
    debtChange > 0 ? 'up' : debtChange < 0 ? 'down' : 'flat';
  const evolutionPoints = useMemo(
    () => buildYearlyEvolutionPoints(totalDebt, contracts),
    [totalDebt, contracts],
  );

  const nextForContract = (id: string) =>
    installmentsByContract[id]?.find((i) => i.status !== 'paid');
  const overdueCountFor = (id: string) =>
    installmentsByContract[id]?.filter((i) => i.status === 'overdue').length ?? 0;

  const openSheet = (mode: ReportMode, contractId: string | null = null) => {
    if (reportsLocked) {
      navigate('/app/profile');
      return;
    }
    setSheetMode(mode);
    setSheetContractId(contractId);
    setSheetOpen(true);
  };

  return (
    <Screen
      headerTitle="Relatórios"
      headerSubtitle="Acompanhamento financeiro consolidado"
      hasTabNavigation
      headerRightIcon={hasContracts && !reportsLocked ? 'icon-extract' : undefined}
      onClickHeaderRight={
        hasContracts && !reportsLocked ? () => openSheet('consolidated') : undefined
      }
    >
      <div className="flex flex-col gap-medium py-medium">
        {!hasContracts ? (
          <EmptyState onAdd={() => navigate('/app/add')} />
        ) : (
          <>
            <Box className="flex flex-col gap-smaller">
              <div className="flex items-center justify-between gap-smaller">
                <Text className="text-captionLG color-intense-06 uppercase tracking-wider">
                  Olá, {user.fullName?.split(' ')[0] || 'produtor'}
                </Text>
                <StatusChip status={financialStatus} size="sm" />
              </div>
              <MetricCard
                label="Endividamento total"
                value={totalSplit.integer}
                decimals={totalSplit.decimal}
                delta={{
                  value: formatSignedCompact(debtChange),
                  direction: debtDirection,
                  style: 'pill',
                  note: 'vs. mês anterior',
                }}
                footer={[
                  {
                    label: `Pago em ${new Date().getFullYear()}`,
                    value: formatCentsToBRLCompact(paidThisYear),
                  },
                  {
                    label: 'Contratos ativos',
                    value: String(contracts.filter((c) => c.status === 'active').length),
                  },
                ]}
              />
            </Box>

            <Box
              className={cn(
                'rounded-medium border px-small py-smaller flex flex-col gap-quark',
                STATUS_CARD_BG[financialStatus],
              )}
            >
              <Text className="text-captionLG color-intense-06 uppercase tracking-wider">
                Análise da sua carteira
              </Text>
              <HumanInsights items={insights} />
            </Box>

            <Box className="rounded-medium bg-intense-02 border border-intense-03 px-smaller pt-quark pb-smaller flex flex-col gap-smaller">
              <ReportTabsBar
                items={[
                  { key: 'overview', label: 'Visão geral' },
                  {
                    key: 'attention',
                    label: 'Vencimentos',
                    count: consolidated.overdueCount + consolidated.upcoming30Count,
                  },
                  { key: 'contracts', label: 'Contratos', count: contracts.length },
                ]}
                active={tab}
                onChange={setTab}
              />

              <GatedTabContent locked={reportsLocked} onUpgrade={() => navigate('/app/profile')}>
                {tab === 'overview' ? (
                  <TabOverview
                    liquidity90={liquidity90}
                    upcoming30Count={alertHero.upcoming30Count}
                    percentPaid={consolidated.percentPaid}
                    totalPaid={consolidated.totalPaid}
                    avgRate={avgRate}
                    evolutionPoints={evolutionPoints}
                  />
                ) : null}

                {tab === 'attention' ? (
                  <TabAttention
                    overdueCount={consolidated.overdueCount}
                    overdueAmount={consolidated.overdueAmount}
                    upcoming7Count={consolidated.upcoming7Count}
                    upcoming30Count={consolidated.upcoming30Count}
                    items={attentionAll}
                    onClickItem={(contractId) => navigate(`/app/contracts/${contractId}`)}
                  />
                ) : null}

                {tab === 'contracts' ? (
                  <TabContracts
                    contracts={contracts}
                    nextForContract={nextForContract}
                    overdueCountFor={overdueCountFor}
                    onOpen={(id) => navigate(`/app/contracts/${id}`)}
                    onExport={(id) => openSheet('contract', id)}
                  />
                ) : null}
              </GatedTabContent>
            </Box>

            <PdfDocumentCard
              locked={reportsLocked}
              onClick={() =>
                reportsLocked ? navigate('/app/profile') : openSheet('consolidated')
              }
            />
          </>
        )}
      </div>

      <GenerateReportSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        contracts={contracts}
        initialMode={sheetMode}
        initialContractId={sheetContractId}
      />
    </Screen>
  );
}

function TabOverview({
  liquidity90,
  upcoming30Count,
  percentPaid,
  totalPaid,
  avgRate,
  evolutionPoints,
}: {
  liquidity90: number;
  upcoming30Count: number;
  percentPaid: number;
  totalPaid: number;
  avgRate: number;
  evolutionPoints: BarChartEvolutionPoint[];
}) {
  return (
    <div className="flex flex-col gap-smaller">
      <Box className="grid grid-cols-3 gap-quark md:gap-smaller">
        <KpiTile
          label="A pagar 90d"
          value={formatCentsToBRLCompact(liquidity90)}
          hint={upcoming30Count > 0 ? `${upcoming30Count} em 30d` : 'sem urgência'}
        />
        <KpiTile
          label="Quitado"
          value={`${percentPaid.toFixed(0)}%`}
          hint={formatCentsToBRLCompact(totalPaid)}
        />
        <KpiTile
          label="Taxa média"
          value={avgRate ? `${(avgRate * 100).toFixed(1)}%` : '—'}
          hint="a.a. ponderada"
        />
      </Box>

      <Box className="rounded-medium bg-intense-01 border border-intense-03 p-small">
        <div className="flex items-center justify-between mb-smaller">
          <Text className="text-paragraphMD color-complement-01">
            Evolução do saldo devedor
          </Text>
          <Text className="text-captionLG color-intense-06">Por ano</Text>
        </div>
        <BarChartEvolution points={evolutionPoints} height={190} />
      </Box>
    </div>
  );
}

function TabAttention({
  overdueCount,
  overdueAmount,
  upcoming7Count,
  upcoming30Count,
  items,
  onClickItem,
}: {
  overdueCount: number;
  overdueAmount: number;
  upcoming7Count: number;
  upcoming30Count: number;
  items: ReturnType<typeof selectAttentionAll>;
  onClickItem: (contractId: string) => void;
}) {
  return (
    <div className="flex flex-col gap-smaller">
      <Box className="grid grid-cols-3 gap-smaller">
        <AttentionTile
          label="Vencidas"
          value={String(overdueCount)}
          tone={overdueCount > 0 ? 'critical' : 'neutral'}
          hint={
            overdueCount > 0 ? formatCentsToBRLCompact(overdueAmount) : 'Tudo em dia'
          }
        />
        <AttentionTile
          label="Próximos 7 dias"
          value={String(upcoming7Count)}
          tone={upcoming7Count > 0 ? 'warning' : 'neutral'}
        />
        <AttentionTile
          label="Próximos 30 dias"
          value={String(upcoming30Count)}
          tone="neutral"
        />
      </Box>

      <Box className="rounded-medium bg-intense-01 border border-intense-03 p-small flex flex-col gap-quark">
        <Text className="text-paragraphMD color-complement-01 mb-quark">
          Parcelas que exigem atenção
        </Text>
        {items.length === 0 ? (
          <Text className="text-bodyMD color-intense-06">
            Sem parcelas a vencer nos próximos 30 dias.
          </Text>
        ) : (
          items.slice(0, 12).map((it) => (
            <ScheduleRow
              key={it.installment.id}
              installment={it.installment}
              contractLabel={
                it.contract
                  ? `${it.contract.institution.name} · #${it.contract.contractNumber}`
                  : undefined
              }
              onClick={() => onClickItem(it.installment.contractId)}
            />
          ))
        )}
      </Box>
    </div>
  );
}

function TabContracts({
  contracts,
  nextForContract,
  overdueCountFor,
  onOpen,
  onExport,
}: {
  contracts: ReturnType<typeof selectContractsList>;
  nextForContract: (id: string) => ReturnType<typeof Object> | undefined;
  overdueCountFor: (id: string) => number;
  onOpen: (id: string) => void;
  onExport: (id: string) => void;
}) {
  return (
    <Box className="grid grid-cols-1 md:grid-cols-2 gap-smaller">
      {contracts.map((c) => (
        <ContractReportRow
          key={c.id}
          contract={c}
          nextInstallment={nextForContract(c.id) as never}
          overdueCount={overdueCountFor(c.id)}
          onClick={() => onOpen(c.id)}
          onExport={() => onExport(c.id)}
        />
      ))}
    </Box>
  );
}

function ReportTabsBar({
  items,
  active,
  onChange,
}: {
  items: ReportTabItem[];
  active: ReportTab;
  onChange: (key: ReportTab) => void;
}) {
  return (
    <div className="relative">
      <div
        role="tablist"
        aria-label="Seções do relatório"
        className="flex items-end gap-medium overflow-x-auto scrollbar-hide -mb-px"
      >
        {items.map((item) => {
          const isActive = item.key === active;
          return (
            <button
              key={item.key}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => onChange(item.key)}
              className={cn(
                'relative inline-flex items-center gap-quark py-smaller transition-colors',
                'focus-visible:outline-none',
              )}
            >
              <Text
                className={cn(
                  'text-bodyMD whitespace-nowrap transition-colors',
                  isActive
                    ? 'color-complement-01 font-semibold'
                    : 'color-intense-06',
                )}
              >
                {item.label}
              </Text>
              {typeof item.count === 'number' && item.count > 0 ? (
                <span
                  className={cn(
                    'inline-flex items-center justify-center min-w-[20px] h-[18px] px-quark rounded-full text-[10px] font-semibold transition-colors',
                    isActive
                      ? 'bg-primary-01 text-intense-01'
                      : 'bg-intense-03 text-intense-06',
                  )}
                >
                  {item.count}
                </span>
              ) : null}
              <span
                aria-hidden
                className={cn(
                  'absolute left-0 right-0 bottom-0 h-[2px] rounded-full transition-colors',
                  isActive ? 'bg-primary-01' : 'bg-transparent',
                )}
              />
            </button>
          );
        })}
      </div>
      <div className="h-px bg-intense-03" />
    </div>
  );
}

function KpiTile({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="rounded-medium bg-intense-01 border border-intense-03 px-smaller py-quark min-w-0">
      <Text className="text-captionMD color-intense-06 uppercase tracking-wide truncate">
        {label}
      </Text>
      <Text className="mt-[2px] text-paragraphLG color-complement-01 font-semibold leading-tight break-words">
        {value}
      </Text>
      {hint ? (
        <Text className="text-captionMD color-intense-06 truncate mt-[2px]">{hint}</Text>
      ) : null}
    </div>
  );
}

function AttentionTile({
  label,
  value,
  hint,
  tone,
}: {
  label: string;
  value: string;
  hint?: string;
  tone: 'critical' | 'warning' | 'neutral';
}) {
  const styles =
    tone === 'critical'
      ? { wrap: 'bg-warning-01 border-warning-02', label: 'color-warning-06', value: 'color-warning-06' }
      : tone === 'warning'
        ? { wrap: 'bg-alert-01 border-alert-02', label: 'color-alert-06', value: 'color-alert-07' }
        : { wrap: 'bg-intense-01 border-intense-03', label: 'color-intense-06', value: 'color-complement-01' };
  return (
    <div className={`rounded-medium border ${styles.wrap} p-small`}>
      <Text className={`text-captionLG uppercase tracking-wider ${styles.label}`}>
        {label}
      </Text>
      <Text className={`mt-quark text-headingH1 break-words ${styles.value}`}>
        {value}
      </Text>
      {hint ? <Text className="text-captionMD color-intense-06">{hint}</Text> : null}
    </div>
  );
}

function GatedTabContent({
  locked,
  onUpgrade,
  children,
}: {
  locked: boolean;
  onUpgrade: () => void;
  children: React.ReactNode;
}) {
  if (!locked) return <>{children}</>;
  return (
    <div className="relative overflow-hidden rounded-medium">
      <div
        aria-hidden
        className="pointer-events-none select-none"
        style={{ filter: 'blur(6px)', opacity: 0.55 }}
      >
        {children}
      </div>
      <div className="absolute inset-0 flex items-center justify-center bg-intense-01/70 backdrop-blur-[2px] px-small py-medium">
        <article className="w-full max-w-md overflow-hidden rounded-medium border border-support-01 bg-intense-01 shadow-level2">
          <div className="flex h-[6px]">
            <div className="w-1/2 bg-primary-01" />
            <div className="w-1/4 bg-secondary-01" />
            <div className="w-1/4 bg-support-01" />
          </div>
          <div className="p-medium">
            <div className="flex items-center gap-quark">
              <span className="inline-flex items-center rounded-full bg-primary-01 px-nano py-quark text-[10px] font-bold uppercase tracking-[0.12em] text-intense-01">
                Premium
              </span>
              <span className="inline-flex items-center gap-quark text-caption color-intense-05">
                <Icon name="icon-graphic-up" size={12} color="#3F7D5F" rounded={false} />
                <Text className="text-captionMD color-intense-06">plano necessário</Text>
              </span>
            </div>
            <Text className="mt-smaller text-headingH2 color-complement-01">
              Análises e relatórios avançados
            </Text>
            <Text className="mt-quark text-bodyMD color-intense-06">
              Liberando esta área você acessa indicadores detalhados, vencimentos
              priorizados, lista completa de contratos e a geração de PDFs
              profissionais.
            </Text>
            <button
              type="button"
              onClick={onUpgrade}
              className="mt-medium inline-flex items-center gap-quark rounded-nano border border-primary-01 px-small py-quark hover:bg-support-01"
            >
              <Text className="text-bodyMD font-medium color-primary-01">Conhecer planos</Text>
              <Icon name="icon-chevron-right" size={14} color="#1D4733" rounded={false} />
            </button>
          </div>
        </article>
      </div>
    </div>
  );
}

function PdfDocumentCard({ locked, onClick }: { locked: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full text-left rounded-medium border border-dashed border-intense-04 bg-intense-02 p-small flex items-center justify-between gap-smaller hover:bg-intense-03 transition-colors"
    >
      <div className="flex items-center gap-smaller min-w-0">
        <div className="w-[40px] h-[40px] rounded-medium bg-intense-01 flex items-center justify-center shrink-0">
          <Icon name="icon-extract" size={18} color="#1D4733" rounded={false} />
        </div>
        <div className="min-w-0">
          <Text className="text-bodyLG color-complement-01">
            {locked ? 'Documento PDF é um recurso Premium' : 'Documento completo em PDF'}
          </Text>
          <Text className="text-captionMD color-intense-06">
            {locked
              ? 'Faça upgrade do plano para gerar e compartilhar relatórios PDF.'
              : 'Capa, sumário executivo, indicadores e detalhamento de cada contrato.'}
          </Text>
        </div>
      </div>
      <div className="shrink-0 flex items-center gap-quark">
        <Text className="text-captionLG color-primary-01 font-semibold">
          {locked ? 'Conhecer planos' : 'Gerar relatório'}
        </Text>
        <Icon name="icon-chevron-right" size={16} color="#1D4733" rounded={false} />
      </div>
    </button>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <Box className="rounded-large bg-primary-01 px-small py-large flex flex-col items-center text-center">
      <Box className="px-smaller py-quark rounded-full bg-intense-01/15 border border-intense-01/25">
        <Text className="text-captionLG color-intense-01 tracking-wider">
          NADA POR AQUI AINDA
        </Text>
      </Box>
      <div className="mt-medium w-[96px] h-[120px] rounded-medium bg-intense-01 flex items-center justify-center shadow-level2">
        <Icon name="icon-extract" size={40} color="#1D4733" rounded={false} />
      </div>
      <Text as="p" className="mt-medium text-headingH1 color-intense-01">
        Cadastre um contrato para ver seus relatórios
      </Text>
      <Text as="p" className="mt-quark text-bodyMD color-intense-01/80 max-w-[400px]">
        Os indicadores, evolução e a geração de PDF aparecem aqui assim que você
        importar ou criar seu primeiro contrato.
      </Text>
      <button
        type="button"
        onClick={onAdd}
        className="mt-medium px-medium py-smaller rounded-small bg-intense-01"
      >
        <Text className="text-paragraphLG color-primary-01">Adicionar contrato</Text>
      </button>
    </Box>
  );
}
